/**
 * Raw field rows for QueryBuilder `fields` prop (Crowdflow / Venue shapes).
 */

/**
 * Injected until API returns these rows; API rows with the same `field` win.
 */
export const LOCAL_CHECKIN_QUERY_FIELDS = [
    { field: 'cf_checkInStatus', label: 'Check-in Status' },
    { field: 'cf_checkInTime', label: 'Check-in Time' },
    { field: 'cf_checkInDeployment', label: 'Check-in Deployment' },
];

/**
 * Merge API field rows with local defaults. Rows later in `apiRows` overwrite
 * earlier entries with the same `field` key (API wins over locals).
 *
 * @param {Array<{ field: string, label: (string|undefined) }>} apiRows
 * @param {Array<{ field: string, label: (string|undefined) }>} [localRows]
 * @returns {Array<{ field: string, label: (string|undefined) }>}
 */
// Pre-compute the keys for easy filtering so we aren't mapping on every render
const CHECKIN_FIELD_KEYS = LOCAL_CHECKIN_QUERY_FIELDS.map((f) => f.field);

export function mergeFieldRowsPreferringApi(apiRows, localRows = []) {
    const byKey = new Map();
    (localRows || []).forEach((row) => {
        if (row?.field) {
            byKey.set(row.field, row);
        }
    });
    (apiRows || []).forEach((row) => {
        if (row?.field) {
            byKey.set(row.field, row);
        }
    });
    return Array.from(byKey.values());
}

/**
 * Same resolution order as Venue QueryBuilder:
 * SponsorFieldList -> ProductFieldList -> AllFieldsUnderProject.
 *
 * @param {object|null|undefined} fields
 * @param {object} [options]
 * @param {boolean} [options.excludeCheckinFields=false]
 *   When true, `LOCAL_CHECKIN_QUERY_FIELDS` are not injected as local
 *   defaults (use for modules where check-in fields are not applicable,
 *   e.g. event participation reports).
 * @returns {Array<object>}
 */
export function getFieldRowsFromQueryBuilderFieldsProp(fields, options = {}) {
    const { excludeCheckinFields = false } = options;
    const localRows = excludeCheckinFields ? [] : LOCAL_CHECKIN_QUERY_FIELDS;
    if (!fields) {
        return mergeFieldRowsPreferringApi([], localRows);
    }

    const raw =
        fields.SponsorFieldList?.fields ||
        fields.ProductFieldList?.fields ||
        fields.AllFieldsUnderProject?.fields ||
        [];
    const apiRows = Array.isArray(raw) ? raw : [];
    return mergeFieldRowsPreferringApi(apiRows, localRows);
}
